import React, { useEffect, useMemo, useRef, useState } from 'react';
import DISEASE_GROUPS, {
  matchAllGroups,
  combineGroupSets,
  medgenUrl,
  SHOW_COMBINE_MODE_TOGGLE,
  SHOW_CLEAR_BUTTON,
  DEFAULT_COMBINE_MODE,
} from './diseaseGroups';
// NCBI's own site mark, saved locally so the links do not reach out to
// ncbi.nlm.nih.gov just to draw an icon.
import ncbiFavicon from './img/ncbi-favicon.png';

/**
 * DiseaseGroupFilter — the curated "Lung Diseases" view.
 *
 * Shows one round toggle per group in diseaseGroups.js, hiding any group that
 * no currently loaded protein matches, and a MedGen link per selected group.
 * Several groups can be on at once; how that combines is DEFAULT_COMBINE_MODE,
 * exposed as a slide switch when SHOW_COMBINE_MODE_TOGGLE is on.
 *
 * Props:
 *   onFilterChange(Set|null) — matching uniprot ids, or null for "no filter"
 *   knownIds {Set|null}      — restrict counts to the proteins in the grid
 */
export default function DiseaseGroupFilter({ onFilterChange, knownIds }) {
  const [meshIndex, setMeshIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [combineMode, setCombineMode] = useState(DEFAULT_COMBINE_MODE); // 'or' | 'and'
  // MainGrid recreates onFilterChange every render; keeping it in a ref stops
  // the filter from re-firing on every render.
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.PUBLIC_URL}/data/mesh-index.json`)
      .then(r => r.json())
      .then(idx => { if (!cancelled) { setMeshIndex(idx); setLoading(false); } })
      .catch(err => {
        console.error('DiseaseGroupFilter: failed to load MeSH data', err);
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Groups with at least one matching protein, in configuration order.
  const available = useMemo(() => {
    if (!meshIndex) return [];
    return matchAllGroups(meshIndex, knownIds, DISEASE_GROUPS)
      .filter(entry => entry.ids.size > 0);
  }, [meshIndex, knownIds]);

  // Drop selections whose group disappeared (e.g. the grid data changed).
  useEffect(() => {
    if (!available.length) return;
    const ids = new Set(available.map(e => e.group.id));
    setSelected(prev => {
      const next = new Set([...prev].filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [available]);

  useEffect(() => {
    if (!available.length) { onFilterChangeRef.current(null); return; }
    const sets = available.filter(e => selected.has(e.group.id)).map(e => e.ids);
    onFilterChangeRef.current(combineGroupSets(sets, combineMode));
  }, [available, selected, combineMode]);

  const toggle = id => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading) {
    return <div className="disease-group-filter"><div className="disease-group-empty">Loading disease groups…</div></div>;
  }
  if (!available.length) {
    return (
      <div className="disease-group-filter">
        <div className="disease-group-empty">No disease group matches the loaded proteins.</div>
      </div>
    );
  }

  const multi = selected.size > 1;
  // Config order, so the links line up with the buttons above them.
  const selectedGroups = available.filter(e => selected.has(e.group.id));

  return (
    <div className="disease-group-filter">
      <div className="disease-group-buttons" role="group" aria-label="Lung disease groups">
        {available.map(({ group, ids }) => {
          const on = selected.has(group.id);
          return (
            <button
              key={group.id}
              type="button"
              className={`disease-group-btn${on ? ' active' : ''}`}
              aria-pressed={on}
              title={group.description}
              onClick={() => toggle(group.id)}
            >
              <span className="disease-group-dot" aria-hidden="true" />
              <span className="disease-group-label">{group.label}</span>
              <span className="disease-group-count">{ids.size}</span>
            </button>
          );
        })}
      </div>

      {selectedGroups.length > 0 && (
        <div className="disease-group-links">
          {selectedGroups.map(({ group }) => {
            const url = medgenUrl(group);
            if (!url) return null;
            // MedGen's own concept name, prefixed with the button label when the
            // two differ, so a link is easy to trace back to its toggle.
            const { name, cui } = group.medgen;
            const text = name.toLowerCase() === group.label.toLowerCase()
              ? name
              : `${group.label}: ${name}`;
            return (
              <a
                key={group.id}
                className="disease-group-link"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open MedGen concept ${cui} in a new tab`}
              >
                <img className="disease-group-link-icon" src={ncbiFavicon} alt="" aria-hidden="true" />
                <span>{text} on MedGen</span>
                {/* Same open-in-new mark the grid uses on its target links. */}
                <span className="material-symbols-outlined protein-list-link-icon disease-group-link-out" aria-hidden="true">
                  open_in_new
                </span>
                <span className="visually-hidden"> (opens in a new tab)</span>
              </a>
            );
          })}
        </div>
      )}

      {(SHOW_COMBINE_MODE_TOGGLE || (SHOW_CLEAR_BUTTON && selected.size > 0)) && (
        <div className="disease-group-controls">
          {SHOW_COMBINE_MODE_TOGGLE && (
            <label className={`disease-group-switch${multi ? '' : ' idle'}`}>
              <input
                type="checkbox"
                checked={combineMode === 'and'}
                onChange={e => setCombineMode(e.target.checked ? 'and' : 'or')}
              />
              <span className="disease-group-switch-track" aria-hidden="true">
                <span className="disease-group-switch-knob" />
              </span>
              <span className="disease-group-switch-text">
                <strong>{combineMode === 'and' ? 'Match all (AND)' : 'Match any (OR)'}</strong>
                <span className="disease-group-switch-hint">
                  {combineMode === 'and'
                    ? 'Only proteins linked to every selected disease.'
                    : 'Proteins linked to at least one selected disease.'}
                </span>
              </span>
            </label>
          )}

          {SHOW_CLEAR_BUTTON && selected.size > 0 && (
            <button
              type="button"
              className="pill-btn"
              onClick={() => setSelected(new Set())}
            >
              Clear disease selection
            </button>
          )}
        </div>
      )}
    </div>
  );
}
